import React from 'react';

// Main logo with full monogram + text
export const LogoFull = ({ isDark = true, className = "", size = "default" }) => {
  const goldColor = "#D4AF37";
  const textColor = isDark ? "#FFFFFF" : "#1A1A1A";
  
  const sizes = {
    small: { height: 32, monogramScale: 0.7 },
    default: { height: 40, monogramScale: 1 },
    large: { height: 56, monogramScale: 1.4 },
  };
  
  const { height, monogramScale } = sizes[size] || sizes.default;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* HB Monogram */}
      <svg 
        width={28 * monogramScale} 
        height={32 * monogramScale} 
        viewBox="0 0 28 32" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        {/* H - Left vertical */}
        <rect x="0" y="0" width="3" height="32" fill={goldColor} />
        {/* H - Right vertical (shared with B) */}
        <rect x="12" y="0" width="3" height="32" fill={goldColor} />
        {/* H - Horizontal connector */}
        <rect x="0" y="14.5" width="15" height="3" fill={goldColor} />
        
        {/* B - Top curve */}
        <path 
          d="M15 0 H22 C25.866 0 29 3.134 29 7 C29 10.866 25.866 14 22 14 H15 V0 Z" 
          fill={goldColor}
          transform="translate(-1, 0) scale(0.97, 1)"
        />
        {/* B - Bottom curve */}
        <path 
          d="M15 18 H23 C26.866 18 30 21.134 30 25 C30 28.866 26.866 32 23 32 H15 V18 Z" 
          fill={goldColor}
          transform="translate(-2, 0) scale(0.97, 1)"
        />
      </svg>
      
      {/* Text */}
      <div className="flex flex-col leading-none">
        <span 
          className="font-playfair font-semibold tracking-wide"
          style={{ 
            color: textColor,
            fontSize: height * 0.45,
            letterSpacing: '0.02em'
          }}
        >
          HB
        </span>
        <span 
          className="uppercase tracking-[0.25em] font-light"
          style={{ 
            color: goldColor,
            fontSize: height * 0.28,
          }}
        >
          Transfert
        </span>
      </div>
    </div>
  );
};

// Monogram only (for favicon, compact spaces)
export const LogoMonogram = ({ color = "#D4AF37", size = 32, className = "" }) => {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 32 32" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* H - Left vertical */}
      <rect x="2" y="2" width="3.5" height="28" fill={color} />
      {/* H - Right vertical (shared with B) */}
      <rect x="13" y="2" width="3.5" height="28" fill={color} />
      {/* H - Horizontal connector */}
      <rect x="2" y="14" width="14.5" height="3.5" fill={color} />
      
      {/* B - Top curve */}
      <path 
        d="M16.5 2 H22 C25.5 2 28 4.5 28 8 C28 11.5 25.5 14 22 14 H16.5 V2 Z" 
        fill={color}
      />
      {/* B - Bottom curve (slightly larger) */}
      <path 
        d="M16.5 17.5 H23 C26.866 17.5 30 20.5 30 24.5 C30 28.5 26.866 30 23 30 H16.5 V17.5 Z" 
        fill={color}
      />
    </svg>
  );
};

// Simple text logo (fallback/alternative)
export const LogoText = ({ isDark = true, className = "" }) => {
  const goldColor = "#D4AF37";
  const textColor = isDark ? "#FFFFFF" : "#1A1A1A";

  return (
    <div className={`flex items-baseline gap-1 ${className}`}>
      <span 
        className="font-playfair text-2xl font-semibold"
        style={{ color: textColor }}
      >
        HB
      </span>
      <span 
        className="font-playfair text-2xl font-semibold"
        style={{ color: goldColor }}
      >
        Transfert
      </span>
    </div>
  );
};

// Inline SVG for header (more refined version matching the original logo)
export const LogoHeader = ({ isDark = true, className = "" }) => {
  const goldColor = "#D4AF37";
  
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {/* Refined HB Monogram matching original */}
      <svg 
        width="36" 
        height="40" 
        viewBox="0 0 36 40" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        {/* H - Left vertical line */}
        <rect x="0" y="0" width="4" height="40" fill={goldColor} />
        {/* H - Right vertical line (shared with B) */}
        <rect x="14" y="0" width="4" height="40" fill={goldColor} />
        {/* H - Horizontal bar */}
        <rect x="0" y="18" width="18" height="4" fill={goldColor} />
        
        {/* B - Top bowl */}
        <path 
          d="M18 0 H26 C30.418 0 34 3.582 34 8 V8 C34 12.418 30.418 16 26 16 H18 V0 Z" 
          fill={goldColor}
        />
        {/* B - Bottom bowl (slightly wider) */}
        <path 
          d="M18 24 H27 C31.971 24 36 28.029 36 33 V33 C36 37.418 32.418 40 28 40 H18 V24 Z" 
          fill={goldColor}
        />
      </svg>
      
      {/* Text - HB TRANSFERT */}
      <div className="flex flex-col items-start leading-none">
        <span 
          className="font-playfair font-semibold text-lg tracking-wide"
          style={{ color: isDark ? '#FFFFFF' : '#1A1A1A' }}
        >
          HB
        </span>
        <span 
          className="uppercase text-[10px] tracking-[0.3em] font-normal"
          style={{ color: goldColor }}
        >
          Transfert
        </span>
      </div>
    </div>
  );
};

// Footer version (larger, more prominent)
export const LogoFooter = ({ isDark = true, className = "" }) => {
  const goldColor = "#D4AF37";
  
  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      {/* Large Monogram */}
      <svg 
        width="48" 
        height="54" 
        viewBox="0 0 36 40" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* H - Left vertical line */}
        <rect x="0" y="0" width="4" height="40" fill={goldColor} />
        {/* H - Right vertical line (shared with B) */}
        <rect x="14" y="0" width="4" height="40" fill={goldColor} />
        {/* H - Horizontal bar */}
        <rect x="0" y="18" width="18" height="4" fill={goldColor} />
        
        {/* B - Top bowl */}
        <path 
          d="M18 0 H26 C30.418 0 34 3.582 34 8 V8 C34 12.418 30.418 16 26 16 H18 V0 Z" 
          fill={goldColor}
        />
        {/* B - Bottom bowl */}
        <path 
          d="M18 24 H27 C31.971 24 36 28.029 36 33 V33 C36 37.418 32.418 40 28 40 H18 V24 Z" 
          fill={goldColor}
        />
      </svg>
      
      {/* Text below */}
      <div className="flex flex-col items-center">
        <span 
          className="font-playfair font-semibold text-2xl tracking-wide"
          style={{ color: isDark ? '#FFFFFF' : '#1A1A1A' }}
        >
          HB <span style={{ color: goldColor }}>Transfert</span>
        </span>
        <span 
          className="uppercase text-[11px] tracking-[0.4em] mt-1"
          style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(26,26,26,0.5)' }}
        >
          Chauffeur Privé
        </span>
      </div>
    </div>
  );
};

export default LogoHeader;